<template>
  <v-row no-gutters class="pt-5 fill-height">
    <v-col class="pr-2" v-if="isMetadataFetched">
      <card-title-row
        translation-path="sidebar.modelingNetworkCheckTitle"
        class="mb-5"
        :has-buttons="false"
      ></card-title-row>
      <sidebar-row class="pt-2">
        <sidebar-select-row
          font-size="14px"
          :value="statusModel"
          :select-props="statusProps"
          @input="statusModel = $event"
          translation-path="sidebar.status"
          :menu-props="{ left: true, offsetX: true, 'nudge-left': 20, 'nudge-top': -5 }"
          align="center"
        />
      </sidebar-row>
      <sidebar-row>
        <sidebar-select-row
          class="my-5"
          font-size="14px"
          :value="netKindModel"
          :select-props="netKindProps"
          @input="netKindModel = $event"
          translation-path="sidebar.networkType"
          :menu-props="{ left: true, offsetX: true, 'nudge-left': 20 }"
          align="center"
        />
      </sidebar-row>
      <sidebar-row icon="mdi-tooltip-check" icon-color="rgba(0,0,0,0.7)">
        <template>
          <v-row no-gutters>
            <v-col cols="auto">
              <base-button translation-path="sidebar.check" class="px-6" @click="getErrorObjects"></base-button>
            </v-col>
          </v-row>
        </template>
      </sidebar-row>
      <main-loader v-if="isFetching"></main-loader>
      <modeling-network-errors
        :errors="computedErrors"
        :error-count="errorCount"
        v-if="errorCount > 0 && isFetched"
        @fixProblem="handleErrorFix($event)"
      ></modeling-network-errors>
      <div v-else-if="errorCount === 0 && isFetched">{{ $i18n.t('sidebar.noErrors') }}</div>
    </v-col>
    <main-loader v-else></main-loader>
  </v-row>
</template>

<script>
import CardTitleRow from '@/components/CardTitleRow';
import SidebarRow from '@/components/SidebarRow';
import BaseButton from '@/components/BaseButton';
import ModelingNetworkErrors from '@/components/ModelingNetworkErrors';
import { call, get } from 'vuex-pathify';
import MainLoader from '@/components/MainLoader';
import SidebarSelectRow from '@/components/SidebarSelectRow';

export default {
  name: 'ModelingNetworkCheck',
  components: { MainLoader, ModelingNetworkErrors, BaseButton, SidebarRow, CardTitleRow, SidebarSelectRow },
  data: () => ({
    isFetching: false,
    isFetched: false,
    isMetadataFetched: false,
    statusModel: localStorage.getItem('networkCheck_statusModel')
      ? JSON.parse(localStorage.getItem('networkCheck_statusModel'))
      : [],
    netKindModel: localStorage.getItem('networkCheck_netKindModel')
      ? JSON.parse(localStorage.getItem('networkCheck_netKindModel'))
      : [],
  }),
  computed: {
    errors: get('epanet/errors'),
    netTypes: get('epanet/netKind'),
    statusItems: get('epanet/status'),
    computedNetTypesItems() {
      const old = this.netTypes.map(value => {
        return {
          value: value,
          text: value,
        };
      });
      old.push({ text: this.$i18n.t('sidebar.noValues'), value: null });
      return old;
    },
    computedStatusItems() {
      const old = this.statusItems.map(value => {
        return {
          value: value,
          text: value,
        };
      });
      old.push({ text: this.$i18n.t('sidebar.noValues'), value: null });
      return old;
    },
    statusProps() {
      return {
        items: this.computedStatusItems,
        multiple: true,
        chips: true,
        autoConvertSelect: false,
        hideDetails: true,
      };
    },
    netKindProps() {
      return {
        items: this.computedNetTypesItems,
        multiple: true,
        chips: true,
        hideDetails: true,
      };
    },
    computedErrors() {
      return this.errors.map(value => {
        return {
          errorType: value.errorType,
          ...value.errors,
          count: value.errors.errors.length,
          tooltipText: this.getTooltipText(value.errors.fixable),
          multipleChoice: this.hasMultipleChoice(value.errorType),
          tableHeaders: this.getTableHeaders(value.errorType),
        };
      });
    },
    errorCount() {
      return this.computedErrors.map(error => error.errors).reduce((a, b) => a + b.length, 0);
    },
  },
  methods: {
    getErrors: call('epanet/getErrors'),
    fixError: call('epanet/fixError'),
    getStatus: call('epanet/getStatus'),
    getNetKind: call('epanet/getNetKind'),
    updateErrors: call('epanet/updateErrors'),

    getTableHeaders(type) {
      if (type === 'null_attributes') {
        return [
          { text: this.$i18n.t('sidebar.dataSource'), value: 'datasource_name', width: '20%' },
          { text: this.$i18n.t('sidebar.objectId'), value: 'id', width: '20%' },
          { text: this.$i18n.t('sidebar.columnName'), value: 'columnName', width: '30%' },
          { text: '', value: 'actions', sortable: false, width: '30%', align: 'end' },
        ];
      } else if (type === 'duplicate_points') {
        return [
          { text: this.$i18n.t('sidebar.dataSource'), value: 'datasource_name' },
          { text: this.$i18n.t('sidebar.objectId'), value: 'id' },
          { text: this.$i18n.t('sidebar.pointsIds'), value: 'pointIds' },
          { text: '', value: 'actions', sortable: false, width: '30%', align: 'end' },
        ];
      } else {
        return [
          { text: this.$i18n.t('sidebar.dataSource'), value: 'datasource_name' },
          { text: this.$i18n.t('sidebar.objectId'), value: 'id' },
          { text: '', value: 'actions', sortable: false, width: '30%', align: 'end' },
        ];
      }
    },
    hasMultipleChoice(type) {
      return type === 'no_parent_pipe' || type === 'bad_parent_pipe' || type === 'null_attributes';
    },
    getTooltipText(value) {
      return value ? this.$i18n.t('sidebar.modelingCheck.fix') : this.$i18n.t('sidebar.modelingCheck.cantFix');
    },
    setLocalStorageItems() {
      localStorage.setItem('networkCheck_statusModel', JSON.stringify(this.statusModel));
      localStorage.setItem('networkCheck_netKindModel', JSON.stringify(this.netKindModel));
    },
    async handleErrorFix(data) {
      const { index, errorType, selected, dataSource, id } = data;
      const payload = {
        errorType: errorType,
        index: index,
        dataSource: dataSource,
        feature: id,
      };
      if (errorType === 'no_parent_pipe' || errorType === 'bad_parent_pipe') {
        payload.parent_pipe_id = selected;
        payload.feature = id;
      } else {
        payload.feature = id;
      }
      const r = await this.fixError(payload);
      if (r.status === 200) {
        await this.updateErrors({ errorType, index });
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('success.problemFixed'),
        });
      } else {
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('errors.fixProblemFailed'),
        });
      }
    },
    async getErrorObjects() {
      this.isFetched = false;
      this.isFetching = true;
      this.setLocalStorageItems();
      try {
        await this.getErrors({
          status: this.statusModel,
          net_kind: this.netKindModel,
        });
        this.isFetched = true;
      } finally {
        this.isFetching = false;
      }
    },
    async init() {
      await Promise.all([this.getStatus({}), this.getNetKind({})]);
      this.isMetadataFetched = true;
    },
  },
  mounted() {
    this.init();
  },
};
</script>

<style scoped></style>
